// src/utils/follow/followErrorHandler.js
import { shouldRetryError } from "../toast/toastHelpers";

/**
 * Follow System Error Handler
 *
 * Centralized error handling utilities for the follow system.
 * Provides consistent error messages, retry logic, and user feedback.
 *
 * Requirements: 1.5, 1.6, 2.5, 6.5, 8.3, 8.4, 8.5
 */

/**
 * Error types for follow operations
 */
export const FOLLOW_ERROR_TYPES = {
  SELF_FOLLOW: "SELF_FOLLOW_NOT_ALLOWED",
  ALREADY_FOLLOWING: "ALREADY_FOLLOWING",
  NOT_FOLLOWING: "NOT_FOLLOWING",
  USER_NOT_FOUND: "USER_NOT_FOUND",
  NETWORK_ERROR: "NETWORK_ERROR",
  TIMEOUT: "TIMEOUT",
  AUTH_ERROR: "AUTH_ERROR",
  RATE_LIMITED: "RATE_LIMITED",
  SERVER_ERROR: "SERVER_ERROR",
  UNKNOWN: "UNKNOWN",
};

/**
 * Classify error type based on error object
 * @param {Error} error - The error object
 * @returns {string} Error type from FOLLOW_ERROR_TYPES
 */
export const classifyFollowError = (error) => {
  if (error.message === "SELF_FOLLOW_NOT_ALLOWED") {
    return FOLLOW_ERROR_TYPES.SELF_FOLLOW;
  }

  if (error.code === "ECONNABORTED") {
    return FOLLOW_ERROR_TYPES.TIMEOUT;
  }

  if (error.request && !error.response) {
    return FOLLOW_ERROR_TYPES.NETWORK_ERROR;
  }

  if (error.response) {
    const status = error.response.status;
    const message = error.response.data?.message?.toLowerCase() || "";

    switch (status) {
      case 400:
        if (message.includes("already following")) {
          return FOLLOW_ERROR_TYPES.ALREADY_FOLLOWING;
        }
        if (message.includes("not following")) {
          return FOLLOW_ERROR_TYPES.NOT_FOLLOWING;
        }
        return FOLLOW_ERROR_TYPES.UNKNOWN;

      case 401:
      case 403:
        return FOLLOW_ERROR_TYPES.AUTH_ERROR;

      case 404:
        return FOLLOW_ERROR_TYPES.USER_NOT_FOUND;

      case 409:
        return FOLLOW_ERROR_TYPES.ALREADY_FOLLOWING;

      case 429:
        return FOLLOW_ERROR_TYPES.RATE_LIMITED;

      case 500:
      case 502:
      case 503:
      case 504:
        return FOLLOW_ERROR_TYPES.SERVER_ERROR;

      default:
        return FOLLOW_ERROR_TYPES.UNKNOWN;
    }
  }

  return FOLLOW_ERROR_TYPES.UNKNOWN;
};

/**
 * Get user-friendly error messages for follow operations
 * @param {string} errorType - Error type from FOLLOW_ERROR_TYPES
 * @param {string} action - The action being performed ('follow' or 'unfollow')
 * @returns {Object} Error message details
 */
export const getFollowErrorMessage = (errorType, action = "follow") => {
  const messages = {
    [FOLLOW_ERROR_TYPES.SELF_FOLLOW]: {
      title: "Can't Follow Yourself",
      message: "You cannot follow your own profile.",
      canRetry: false,
      severity: "warning",
    },
    [FOLLOW_ERROR_TYPES.ALREADY_FOLLOWING]: {
      title: "Already Following",
      message: "You are already following this user.",
      canRetry: false,
      severity: "info",
    },
    [FOLLOW_ERROR_TYPES.NOT_FOLLOWING]: {
      title: "Not Following",
      message: "You are not currently following this user.",
      canRetry: false,
      severity: "info",
    },
    [FOLLOW_ERROR_TYPES.USER_NOT_FOUND]: {
      title: "User Not Found",
      message:
        "The user you're trying to follow doesn't exist or has been removed.",
      canRetry: false,
      severity: "error",
    },
    [FOLLOW_ERROR_TYPES.NETWORK_ERROR]: {
      title: "Connection Problem",
      message: "Please check your internet connection and try again.",
      canRetry: true,
      severity: "error",
    },
    [FOLLOW_ERROR_TYPES.TIMEOUT]: {
      title: "Request Timeout",
      message: "The request took too long. Please try again.",
      canRetry: true,
      severity: "error",
    },
    [FOLLOW_ERROR_TYPES.AUTH_ERROR]: {
      title: "Authentication Error",
      message: "Please refresh the page and sign in again.",
      canRetry: false,
      severity: "error",
    },
    [FOLLOW_ERROR_TYPES.RATE_LIMITED]: {
      title: "Too Many Requests",
      message:
        "You're doing that too often. Please wait a moment and try again.",
      canRetry: true,
      severity: "warning",
    },
    [FOLLOW_ERROR_TYPES.SERVER_ERROR]: {
      title: "Server Error",
      message: "Our servers are having trouble. Please try again in a moment.",
      canRetry: true,
      severity: "error",
    },
    [FOLLOW_ERROR_TYPES.UNKNOWN]: {
      title: `${action === "follow" ? "Follow" : "Unfollow"} Failed`,
      message: `Failed to ${action} user. Please try again.`,
      canRetry: true,
      severity: "error",
    },
  };

  return messages[errorType] || messages[FOLLOW_ERROR_TYPES.UNKNOWN];
};

/**
 * Handle follow operation errors with consistent messaging and retry logic
 * @param {Error} error - The error object
 * @param {string} action - The action being performed ('follow' or 'unfollow')
 * @param {Function} toast - Toast notification function
 * @param {Function} retryFn - Optional retry function
 * @returns {Object} Error handling result
 */
export const handleFollowError = (error, action, toast, retryFn = null) => {
  const errorType = classifyFollowError(error);
  const errorDetails = getFollowErrorMessage(errorType, action);

  // Prepare toast options
  const toastOptions = {
    duration: errorDetails.severity === "error" ? 6000 : 4000,
  };

  // Add retry action if error is retryable and retry function is provided
  if (errorDetails.canRetry && retryFn && shouldRetryError(error, 0)) {
    toastOptions.action = {
      label: "Retry",
      onClick: retryFn,
    };
  }

  // Show appropriate toast based on severity
  switch (errorDetails.severity) {
    case "error":
      toast.error(errorDetails.message, toastOptions);
      break;
    case "warning":
      toast.warning(errorDetails.message, toastOptions);
      break;
    case "info":
      toast.info(errorDetails.message, toastOptions);
      break;
    default:
      toast.error(errorDetails.message, toastOptions);
  }

  return {
    errorType,
    errorDetails,
    canRetry: errorDetails.canRetry,
  };
};

/**
 * Create a retry function for follow operations
 * @param {Function} originalFn - The original follow/unfollow function
 * @param {Array} args - Arguments to pass to the original function
 * @param {Function} toast - Toast notification function
 * @returns {Function} Retry function
 */
export const createFollowRetryFn = (originalFn, args, toast) => {
  return async () => {
    try {
      await originalFn(...args);
      toast.success("Action completed successfully!");
    } catch (retryError) {
      // If retry fails, show simple error without retry button
      const errorType = classifyFollowError(retryError);
      const errorDetails = getFollowErrorMessage(
        errorType,
        args[0] ? "follow" : "unfollow"
      );
      toast.error(`Retry failed: ${errorDetails.message}`);
    }
  };
};

/**
 * Validate follow operation parameters
 * @param {string} userId - User ID to validate
 * @param {string} currentUserId - Current user's ID
 * @returns {Object} Validation result
 */
export const validateFollowOperation = (userId, currentUserId) => {
  if (!userId) {
    return {
      isValid: false,
      error: "User ID is required",
      errorType: FOLLOW_ERROR_TYPES.UNKNOWN,
    };
  }

  if (userId === currentUserId) {
    return {
      isValid: false,
      error: "Cannot follow yourself",
      errorType: FOLLOW_ERROR_TYPES.SELF_FOLLOW,
    };
  }

  // Basic MongoDB ObjectId validation (24 hex characters)
  if (!/^[0-9a-fA-F]{24}$/.test(userId)) {
    return {
      isValid: false,
      error: "Invalid user ID format",
      errorType: FOLLOW_ERROR_TYPES.USER_NOT_FOUND,
    };
  }

  return {
    isValid: true,
    error: null,
    errorType: null,
  };
};
